import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/Quotation.css';

const SEND_QUOTATION_EMAIL = `
  query SendMail(
    $bookingId: String!
    $info: String!
    $quotationVersionId: String
  ) {
    sendMail(
      bookingId: $bookingId
      info: $info
      quotationVersionId: $quotationVersionId
    )
  }
`;

export function currentCompanyName(fallback) {
  return (
    String(fallback || '').trim() ||
    Cookies.get('companyName') ||
    'our team'
  );
}

/** Default subject + message for sending an invoice, signed by the company. */
export function invoiceEmailDraft(invoiceNumber) {
  const name = currentCompanyName();
  const number = String(invoiceNumber || '').trim();
  return {
    subject: `Invoice ${number ? `${number} ` : ''}from ${name}`,
    message: `Hello,\n\nPlease find your invoice attached below.\n\nThank you,\n${name}`,
  };
}

export function quotationEmailDraft({ quotationNumber, companyName, clientFirstName }) {
  const name = currentCompanyName(companyName);
  const number = String(quotationNumber || '').trim();
  const first = String(clientFirstName || '').trim();
  const hi = first ? `Hi ${first},` : 'Hi,';
  return {
    subject: number
      ? `Quotation ${number} from ${name}`
      : `Quotation from ${name}`,
    body:
      `${hi}\n\nYour cleaning service quotation is ready. Please review the service scope, tasks, pricing and terms using the secure quotation link. You can accept, request changes or decline from the quotation page. A PDF copy will also be attached.\n\nKind regards,\n${name}`,
  };
}

/** Draft, rejected and change-requested versions can be (re)issued to the client. */
export function canSendQuotation(q) {
  if (!q) return false;
  const stage = String(q.quotationStage || '').toUpperCase();
  const client = String(q.clientStatus || '').toUpperCase();
  if (client === 'ACCEPTED') return false;
  return (
    stage === 'DRAFT' ||
    client === 'REJECTED' ||
    client === 'CHANGE_REQUESTED'
  );
}

/** "Send Quotation Email" popup — issues one quotation version to the client. */
export default function QuotationSendEmailModal({
  bookingId,
  quote,
  clientName,
  companyName,
  onClose,
  onSent,
}) {
  const [sending, setSending] = useState(false);
  const [emailNote, setEmailNote] = useState(() => ({
    ...quotationEmailDraft({
      quotationNumber: quote?.quotationNumber,
      companyName,
      clientFirstName: String(clientName || '').trim().split(/\s+/)[0],
    }),
    cc: '',
  }));

  const send = async () => {
    if (!bookingId || !quote?.id) return;
    try {
      setSending(true);
      const info = JSON.stringify({
        subject: emailNote.subject,
        message: emailNote.body,
        cc: emailNote.cc
          ? emailNote.cc.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      });
      await graphqlRequest(SEND_QUOTATION_EMAIL, {
        bookingId,
        info,
        quotationVersionId: quote.id,
      });
      toast.success('Quotation sent. The email is on its way.');
      onSent?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-box quotation-send">
        <div className="popup-header">
          <h3>
            Send Quotation Email
            {quote?.quotationNumber ? ` – ${quote.quotationNumber}` : ''}
          </h3>
          <button className="popup-close" type="button" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="popup-content">
          <div className="quotation-send-field">
            <label>Subject</label>
            <input
              value={emailNote.subject}
              onChange={(e) => setEmailNote((p) => ({ ...p, subject: e.target.value }))}
            />
          </div>
          <div className="quotation-send-field">
            <label>Message</label>
            <textarea
              rows={7}
              value={emailNote.body}
              onChange={(e) => setEmailNote((p) => ({ ...p, body: e.target.value }))}
            />
          </div>
          <div className="quotation-send-field">
            <label>CC (optional)</label>
            <input
              value={emailNote.cc}
              onChange={(e) => setEmailNote((p) => ({ ...p, cc: e.target.value }))}
            />
          </div>
          <button
            className="btn-send-email"
            type="button"
            disabled={sending || !quote?.id || !bookingId}
            onClick={send}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
